import { reactive, nextTick, type Ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { routerPath } from "@/common/router";
import {
  OperationInput,
  DeleteRegularDeliveryMutation,
  DeleteRegularDeliveryMutationVariables,
  DeleteRegularDeliveryDocument,
} from "@/generated/graphql";
import searchConditionStore from "@/store/searchConditionStore";
import regularDeliveryStore from "@/store/regularDeliveryStore";
import type { ListData, DeliveryAvailabilityData } from "../types";
import { REGULAR_DELIVERY_LIST_FILE_NAME } from "../constants";
import { SCHEDULED_DELIVERY_LIST_FILE_NAME } from "../../admin/ScheduledDeliveryList/constants";

interface UseRegularDeliveryActionsParams {
  searchConditionMemberId: Ref<string | undefined | null>;
  searchConditionName: Ref<string | undefined | null>;
  searchConditionAreaCode: Ref<string | undefined | null>;
  selectListCheckbox: Ref<Array<Record<string, unknown>>>;
  confirmDeleteDialog: Ref<boolean>;
  overlay: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  deliveryAvailabilityStore: Ref<DeliveryAvailabilityData[][]>;
  searchQuery: () => Promise<void>;
}

/**
 * 画面の操作イベントに関するハンドラをまとめて提供する composable。
 *
 * @returns 画面イベントから呼び出す操作ハンドラ群
 */
export const useRegularDeliveryActions = ({
  searchConditionMemberId,
  searchConditionName,
  searchConditionAreaCode,
  selectListCheckbox,
  confirmDeleteDialog,
  overlay,
  errdialog,
  errMessage,
  deliveryAvailabilityStore,
  searchQuery,
}: UseRegularDeliveryActionsParams) => {
  const router = useRouter();
  const store = useStore();

  //  配達依頼作成 logInput
  const operationInput = reactive<OperationInput>({
    area_code: "10",
    detail: "操作詳細",
    display_category: "画面名",
    operation: "操作",
    user_id: localStorageGetUserId(),
  });

  /**
   * 編集ボタン @click
   */
  const onClickEdit = async (item: ListData): Promise<void> => {
    //検索条件を保持する
    searchConditionStore.setCondition({
      view_name: REGULAR_DELIVERY_LIST_FILE_NAME,
      conditions: new Map()
        .set("member_id", searchConditionMemberId.value)
        .set("member_name", searchConditionName.value)
        .set("area_code", searchConditionAreaCode.value),
    });

    // 「定期配達登録-入力」画面へ遷移
    await regularDeliveryStore.setupValue({
      zip_code: "",
      pref_name: "",
      user_id: String(item.memberId),
      areaDialog: false,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      regular_delivery_no: item.regular_delivery_no,
      processFlag: 1,
    });
    await router.push({ path: routerPath.regularDeliveryRegister });
    store.commit("listEditOn");
  };

  /**
   * 店舗削除アイコン @Click
   */
  const onClickDelete = async (item: ListData): Promise<void> => {
    //検索条件を保持する
    searchConditionStore.setCondition({
      view_name: SCHEDULED_DELIVERY_LIST_FILE_NAME,
      conditions: new Map()
        .set("member_id", searchConditionMemberId.value)
        .set("member_name", searchConditionName.value)
        .set("area_code", searchConditionAreaCode.value),
    });

    // 「定期配達登録-入力」画面へ遷移
    await regularDeliveryStore.setupValue({
      zip_code: "",
      pref_name: "",
      user_id: "",
      areaDialog: false,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      regular_delivery_no: item.regular_delivery_no,
      processFlag: 2,
    });
    await router.push({ path: routerPath.regularDeliveryRegister });
    store.commit("listEditOn");
  };

  /**
   * 一括削除 @Click
   */
  const onClickCheckDelete = async (): Promise<void> => {
    if (selectListCheckbox.value.length === 0) {
      return;
    }
    confirmDeleteDialog.value = true;
  };

  /**
   * 削除処理GraphQL実行(1件ずつ)
   */
  const deleteExecute = async (
    regular_delivery_no: string
  ): Promise<boolean> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 配達依頼削除
    try {
      await apolloClient.mutate<
        DeleteRegularDeliveryMutation,
        DeleteRegularDeliveryMutationVariables
      >({
        mutation: DeleteRegularDeliveryDocument,
        variables: {
          regular_delivery_no: regular_delivery_no,
          logInput: operationInput,
        },
      });
      return true;
    } catch (err: unknown) {
      console.error("err=", err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "定期配送データの削除に失敗しました。";
      } else {
        errMessage.value = "予期せぬエラーが発生しました。";
      }
      return false;
    }
  };

  /**
   * 一括削除 実行 @Click
   */
  const onClickCheckDeleteComplete = async (): Promise<void> => {
    const arrayError: string[] = [];

    try {
      overlay.value = true;

      operationInput.detail = "操作詳細";
      operationInput.display_category = "定期配送一括削除";
      operationInput.operation = "定期配送一括削除";

      //1件ずつ削除する
      for (const val of selectListCheckbox.value) {
        if (val !== undefined && val !== null) {
          //データを削除する
          const no = String(val["regular_delivery_no"] ?? "");
          if (!(await deleteExecute(no))) {
            arrayError.push(no);
          }
        }
      }

      // 選択内容クリア
      selectListCheckbox.value = [];

      //画面を閉じる
      confirmDeleteDialog.value = false;
      overlay.value = false;

      //再検索する
      nextTick(async () => {
        await searchQuery();
      });
    } catch (err: unknown) {
      console.error("err=", err);
      errdialog.value = true;
      errMessage.value = "受付番号 " + arrayError.join(",") + "の削除に失敗しました。";
      return;
    } finally {
      // displayCompletedDialog = false;
      overlay.value = false;
    }
  };

  return {
    onClickEdit,
    onClickDelete,
    onClickCheckDelete,
    onClickCheckDeleteComplete,
  };
};
